import { useState } from 'react';

interface Capability {
  title: string;
  description: string;
  icon: string;
}

export default function Capabilities() {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const capabilities: Capability[] = [
    {
      title: 'Website Builder',
      description: 'Crafting responsive, performant websites that blend aesthetics with functionality. From concept to deployment, I build digital homes for brands.',
      icon: 'ri-window-line',
    },
    {
      title: 'UI/UX Designer',
      description: 'Designing interfaces that prioritize user needs. Every interaction is intentional, every flow is optimized, every detail serves a purpose.',
      icon: 'ri-layout-line',
    },
    {
      title: 'Graphic Designer',
      description: 'Creating visual identities that communicate clearly and memorably. Logos, brand systems, and marketing materials that stand out.',
      icon: 'ri-palette-line',
    },
    {
      title: 'Video Editor',
      description: 'Transforming raw footage into compelling narratives. Motion, pacing, and storytelling come together to create engaging visual content.',
      icon: 'ri-film-line',
    },
    {
      title: 'Social Media Manager',
      description: 'Building and nurturing online communities. Strategic content planning, engagement optimization, and brand voice consistency across platforms.',
      icon: 'ri-share-line',
    },
  ];

  return (
    <section className="relative py-32 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <h2
          className="text-5xl md:text-6xl font-bold text-black mb-20 leading-tight text-center"
          style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.02em' }}
        >
          What I Do
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {capabilities.map((capability, index) => (
            <div
              key={index}
              className="group relative backdrop-blur-xl bg-white/60 border border-white/80 rounded-3xl p-8 transition-all duration-500 hover:bg-white/80 hover:shadow-2xl cursor-pointer"
              style={{
                transform: hoveredIndex === index ? 'translateY(-8px)' : 'translateY(0)',
              }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              <div className="relative z-10">
                <div className="w-14 h-14 flex items-center justify-center mb-6">
                  <i className={`${capability.icon} text-4xl text-black`}></i>
                </div>

                <h3
                  className="text-2xl font-semibold text-black mb-4"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {capability.title}
                </h3>

                <p
                  className="text-base text-black/60 leading-relaxed"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {capability.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
